import { useState } from 'react';
import { useAuth } from '@/features/auth';
import { useAsyncState } from '@/shared/hooks/useAsyncState';
import { checkSuperAdminPermission } from '@/features/superadmin/utils/superadmin';
import { ROLES } from '@/features/auth/types';
import { superadminService, CreateUserDTO, UpdateUserDTO, User } from '../services/superadmin.service';
import { CreateUserData, UpdateUserData } from '../types/superadmin';
import { UserRole, UserStatus } from '@/shared/enums';

interface FormUserData {
  id: string;
  email?: string;
  username?: string; // Nome de usuário único
  first_name?: string;
  last_name?: string;
  role?: string;
  status?: 'PENDING' | 'ACTIVE' | 'REJECTED';
  tenant_id?: string | null;
  is_superadmin?: boolean;
}

export const useSuperadminUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [hasAdminApiAccess, setHasAdminApiAccess] = useState<boolean | null>(null);
  const { loading, error, handleAsyncOperation } = useAsyncState();
  const { state: { user } } = useAuth();

  const isSuperAdmin = user?.role === ROLES.SUPER_ADMIN;

  const fetchUsers = async () => {
    await handleAsyncOperation(async () => {
      checkSuperAdminPermission(user?.role);

      try {
        const users = await superadminService.getUsers();
        setUsers(users);
        setHasAdminApiAccess(true);
      } catch (error) {
        console.error("Erro ao buscar usuários:", error);
        setHasAdminApiAccess(false);
        
        // Se houver erro de permissão, retornar dados mascarados
        if ((error as any)?.response?.status === 403) {
          const maskedUsers = users.map(user => ({
            ...user,
            email: '***@***.com'
          }));
          setUsers(maskedUsers);
        } else {
          throw error;
        }
      }
    });
  };

  // Criar um novo usuário
  const createUser = async (userData: CreateUserData) => {
    return handleAsyncOperation<{ success: boolean }>(async () => {
      checkSuperAdminPermission(user?.role);

      // Converter para o tipo correto do DTO
      const createUserDTO: CreateUserDTO = {
        email: userData.email,
        username: userData.username,
        password: userData.password,
        first_name: userData.first_name,
        last_name: userData.last_name,
        role: userData.role as UserRole,
        status: userData.status as UserStatus,
        tenant_id: userData.tenant_id || null,
      };

      await superadminService.createUser(createUserDTO);
      await fetchUsers();
      return { success: true };
    }, 'Erro ao criar usuário');
  };

  // Atualizar um usuário existente
  const updateUser = async (userData: FormUserData) => {
    return handleAsyncOperation<{ success: boolean, data?: User }>(async () => {
      checkSuperAdminPermission(user?.role);

      const { id, ...data } = userData;
      
      console.log('useSuperadminUsers.updateUser - Dados recebidos:', userData);
      
      // Preparar dados para envio, convertendo tipos conforme necessário
      const requestData: any = {};

      if (data.email !== undefined) {
        requestData.email = data.email;
      }
      if (data.username !== undefined) {
        requestData.username = data.username;
      }
      if (data.first_name !== undefined) {
        requestData.first_name = data.first_name;
      }
      if (data.last_name !== undefined) {
        requestData.last_name = data.last_name;
      }
      if (data.role !== undefined) {
        requestData.role = data.role;
      }
      if (data.status !== undefined) {
        requestData.status = data.status;
      }
      if (data.tenant_id !== undefined) {
        // Manter como string (UUID) se não for null/empty
        if (data.tenant_id === null || data.tenant_id === '' || data.tenant_id === 'null') {
          requestData.tenant_id = null;
        } else {
          requestData.tenant_id = data.tenant_id;
        }
      }
      
      console.log('useSuperadminUsers.updateUser - Request data preparado:', requestData);

      const updatedUser = await superadminService.updateUser(id, requestData);

      console.log('useSuperadminUsers.updateUser - Usuário atualizado:', updatedUser);

      // Atualizar o usuário na lista local
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === id ? { ...u, ...updatedUser } : u
        )
      );

      return { success: true, data: updatedUser };
    }, 'Erro ao atualizar usuário').catch(error => {
      console.error('useSuperadminUsers.updateUser - Error capturado:', error);
      return { success: false, message: 'Erro ao atualizar usuário' };
    });
  };

  // Verificar dependências de um usuário
  const checkUserDependencies = async (userId: string) => {
    return handleAsyncOperation(async () => {
      checkSuperAdminPermission(user?.role);
      return await superadminService.checkUserDependencies(userId);
    }, 'Erro ao verificar dependências do usuário');
  };

  // Excluir um usuário
  const deleteUser = async (userId: string, forceUnlink: boolean = false) => {
    return handleAsyncOperation<{ success: boolean }>(async () => {
      checkSuperAdminPermission(user?.role);

      // O superadminService.deleteUser espera um UUID (string), não um número
      await superadminService.deleteUser(userId, forceUnlink);
      // Atualizar a lista local
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
      return { success: true };
    }, 'Erro ao deletar usuário');
  };

  // Resetar senha de um usuário
  // Sem handleAsyncOperation: o diálogo precisa receber o erro para não exibir sucesso falso.
  const resetUserPassword = async (userId: string, newPassword: string) => {
    checkSuperAdminPermission(user?.role);
    await superadminService.resetPassword(userId, newPassword);
    return { success: true };
  };
  
  return {
    users,
    loading,
    error,
    hasAdminApiAccess,
    fetchUsers,
    createUser,
    updateUser,
    checkUserDependencies,
    deleteUser,
    resetPassword: resetUserPassword
  };
}; 